/* eslint-disable no-nested-ternary */
import { MouseEventHandler, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import DevLicense from 'typings/DevLicense';
import { Table, Tbody, Td, Th, Thead, Tr, Line } from './styles';

type SortOrder = 'ascn' | 'desc'; // 'ascending' | 'descending'

function calculeDate(time: string | number | Date) {
  if (time === "never")
    return 'Infinity';

  const x = new Date();
  const y = new Date(time);

  const diff = y.getTime() - x.getTime();

  const days = Math.floor(diff / 86400000);

  return days;
}

function sortData({
  tableData,
  sortKey,
  reverse,
}: {
  tableData: DevLicense[];
  sortKey: keyof DevLicense;
  reverse: boolean;
}) {
  if (!sortKey) return tableData;

  const sortedData = tableData.sort((a, b) => {
    return a[sortKey] > b[sortKey] ? 1 : -1;
  });

  if (reverse) {
    return sortedData.reverse();
  }

  return sortedData;
}

function SortButton({
  sortOrder,
  columnKey,
  sortKey,
  onClick,
}: {
  sortOrder: SortOrder;
  columnKey: keyof DevLicense;
  sortKey: keyof DevLicense;
  onClick: MouseEventHandler<HTMLButtonElement>;
}) {
  return (
    // prettier-ignore
    // eslint-disable-next-line react/button-has-type
    <button
      onClick={onClick}
      className={`${sortKey === columnKey && sortOrder === 'desc'
        ? 'sort-button sort-reverse'
        : 'sort-button'
        }`}
    >
      ▲
    </button>
  );
}

function SortableTable({ data }: { data: DevLicense[] }) {
  const history = useHistory();
  const [sortKey, setSortKey] = useState<keyof DevLicense>('device');
  const [sortOrder, setSortOrder] = useState<SortOrder>('ascn');

  const headers: { key: keyof DevLicense; label: string }[] = [
    { key: 'device', label: 'Device' },
    { key: 'type', label: 'Type' },
    { key: 'owner', label: 'Owner' },
    { key: 'expireDate', label: 'Remain Time (days)' },
    { key: 'remainReference', label: 'Due Date' },
    { key: 'status', label: 'Status' },
  ];

  const sortedData = useCallback(
    () => sortData({ tableData: data, sortKey, reverse: sortOrder === 'desc' }),
    [data, sortKey, sortOrder],
  );

  function changeSort(key: keyof DevLicense) {
    setSortOrder(sortOrder === 'ascn' ? 'desc' : 'ascn');
    setSortKey(key);
  }

  return (
    <Table>
      <Thead>
        <Tr>
          {headers.map(row => {
            return (
              <Th key={row.key}>
                {row.label}{' '}
                <SortButton
                  columnKey={row.key}
                  onClick={() => changeSort(row.key)}
                  {...{
                    sortOrder,
                    sortKey,
                  }}
                />
              </Th>
            );
          })}
        </Tr>
      </Thead>

      <Line />

      <Tbody>
        {sortedData().map(person => {
          return (
            <Tr
              onClick={() => {
                history.push(`./details/${person.device}`, {
                  serial: person.device,
                });
              }}
            >
              <Td>{person.device}</Td>
              <Td>{person.type}</Td>
              <Td>{person.owner}</Td>
              <Td>{calculeDate(person.expireDate)}</Td>
              <Td
                backgroundColor={
                  calculeDate(person.expireDate) > 0
                    ? calculeDate(person.expireDate) >= 0 &&
                      calculeDate(person.expireDate) <= 30
                      ? 'var(--color-system-orange)'
                      : calculeDate(person.expireDate) > 30 &&
                        calculeDate(person.expireDate) < 90
                        ? 'var(--color-system-yellow)'
                        : 'var(--color-system-green)'
                    : 'var(--color-system-red)'
                }
              >
                {person.expireDate}
              </Td>
              <Td
                fontWeight="700"
                color={
                  person.status === 'ONGOING'
                    ? 'var(--color-system-green)'
                    : 'var(--color-system-red)'
                }
              >
                {person.status}
              </Td>
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );
}

export default SortableTable;
